import { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import type { ConversationListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
} from "@tanstack/react-table";
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { useConversations } from "../hooks/use-conversation-data";
import { getConversationsTableColumns } from "./columns/conversations-columns";

interface ConversationsTableContextValue {
  data: ConversationListItemResponse[];
  columns: ColumnDef<ConversationListItemResponse, unknown>[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
  focusedConversationId: ConversationId | null;
  focusedConversation: ConversationListItemResponse | null;
  setFocusedConversationId: (id: ConversationId | null) => void;
  selectedConversationId: ConversationId | null;
  openConversation: (id?: ConversationId | null) => void;
  navigateUp: () => void;
  navigateDown: () => void;
  actionMenuOpenForId: ConversationId | null;
  setActionMenuOpenForId: (id: ConversationId | null) => void;
  columnOrder: ColumnOrderState;
  setColumnOrder: (order: ColumnOrderState) => void;
  columnSizing: ColumnSizingState;
  setColumnSizing: (sizing: ColumnSizingState) => void;
}

const ConversationsTableContext =
  createContext<ConversationsTableContextValue | null>(null);

export function useConversationsTableContext(): ConversationsTableContextValue {
  const context = useContext(ConversationsTableContext);
  if (!context) {
    throw new Error(
      "useConversationsTableContext must be used within a ConversationsTableProvider",
    );
  }
  return context;
}

export const ConversationsTableProvider = memo(
  function ConversationsTableProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { teamId, conversationId: conversationIdParam } = useParams<{
      teamId: string;
      conversationId: string;
    }>();

    // Data is already filtered via useQueryOptions() inside useConversations()
    const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isLoading } =
      useConversations();

    // On the detail view, selectedConversationId comes from the route param
    const selectedConversationId = conversationIdParam
      ? ConversationId.make(conversationIdParam)
      : null;

    // focusedConversationId: on table view, read from ?focused= query param;
    // on detail view, use the :conversationId route param
    const focusedConversationId = useMemo<ConversationId | null>(() => {
      if (selectedConversationId) {
        return selectedConversationId;
      }
      const searchParams = new URLSearchParams(location.search);
      const focused = searchParams.get("focused");
      return focused ? ConversationId.make(focused) : null;
    }, [selectedConversationId, location.search]);

    const setFocusedConversationId = useCallback(
      (id: ConversationId | null) => {
        // Only update URL on table view (no conversationId route param)
        if (selectedConversationId) {
          return;
        }
        const searchParams = new URLSearchParams(location.search);
        if (id) {
          searchParams.set("focused", id);
        } else {
          searchParams.delete("focused");
        }
        const newSearch = searchParams.toString();
        navigate(
          { search: newSearch ? `?${newSearch}` : "" },
          { replace: true },
        );
      },
      [selectedConversationId, location.search, navigate],
    );

    const openConversation = useCallback(
      (id?: ConversationId | null) => {
        const targetId = id ?? focusedConversationId;
        if (targetId && teamId) {
          navigate(
            `/stores/${teamId}/support-v2/${targetId}${location.search}`,
          );
        }
      },
      [focusedConversationId, teamId, navigate, location.search],
    );

    const [actionMenuOpenForId, setActionMenuOpenForId] =
      useState<ConversationId | null>(null);

    const [columnOrder, setColumnOrder] = useLocalStorage<ColumnOrderState>(
      "redo.support-v2.conversations-table.column-order",
      [],
    );
    const [columnSizing, setColumnSizing] = useLocalStorage<ColumnSizingState>(
      "redo.support-v2.conversations-table.column-sizing",
      {},
    );

    const columns = useMemo(
      () => getConversationsTableColumns({ enablePinning: true }),
      [],
    );

    const focusedConversation = useMemo(() => {
      if (!focusedConversationId) {
        return null;
      }
      return data.find((c) => c.id === focusedConversationId) ?? null;
    }, [data, focusedConversationId]);

    const navigateUp = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedConversationId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setFocusedConversationId(data[data.length - 1].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedConversationId,
      );
      if (currentIndex <= 0) {
        return;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFocusedConversationId(data[currentIndex - 1].id);
    }, [data, focusedConversationId, setFocusedConversationId]);

    const navigateDown = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedConversationId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setFocusedConversationId(data[0].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedConversationId,
      );
      if (currentIndex === -1 || currentIndex >= data.length - 1) {
        return;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFocusedConversationId(data[currentIndex + 1].id);
    }, [data, focusedConversationId, setFocusedConversationId]);

    const value = useMemo<ConversationsTableContextValue>(
      () => ({
        data,
        columns,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        isLoading,
        focusedConversationId,
        focusedConversation,
        setFocusedConversationId,
        selectedConversationId,
        openConversation,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
        setActionMenuOpenForId,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
      }),
      [
        data,
        columns,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        isLoading,
        focusedConversationId,
        focusedConversation,
        setFocusedConversationId,
        selectedConversationId,
        openConversation,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
      ],
    );

    return (
      <ConversationsTableContext.Provider value={value}>
        {children}
      </ConversationsTableContext.Provider>
    );
  },
);
